import { Component, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ButtonToastComponent } from 'button-toast-lib';
import { SendOnBehalfOf } from 'enums-lib';
import { FeatureFlagService } from 'feature-flags-lib';
import { finalize } from 'rxjs/operators';
import { environment } from '../../../../../environments/environment';
import { Adv2ConfigService } from '../../../../services/adv2-config.service';
import { ServiceProviderServiceFactory } from '../../../../services/service-providers/service-provider-service.factory';
import { ISignatureProviderComponent } from '../isignatureprovidercomponent';
import { DocusignAuthServer } from './docusign-auth-server.enum';

@Component({
    selector: 'app-signature-docusign',
    templateUrl: './docusign.component.html',
    standalone: false
})
export class DocusignComponent implements ISignatureProviderComponent, OnInit {
  static readonly NAME = 'docusign';
  static readonly LABEL = 'Docusign';

  @ViewChild(ButtonToastComponent)
  buttonToastComponent: ButtonToastComponent;

  testCredentialsInProgress = false;

  private _formGroup: UntypedFormGroup;

  private static UUID_REGEX = new RegExp('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$');

  constructor(private adv2ConfigService: Adv2ConfigService,
              private sps: ServiceProviderServiceFactory,
              protected featureFlagService: FeatureFlagService
  ){}

  get name() {
    return DocusignComponent.NAME;
  }

  get formGroup() {
    return this._formGroup;
  }

  initialiseForm(signatureConfig: any): UntypedFormGroup {
    this.formGroup.get('accountId').setValue(signatureConfig.accountId);
    this.formGroup.get('userId').setValue(signatureConfig.userId);
    this.formGroup.get('useDemo').setValue(signatureConfig.server !== DocusignAuthServer.PRODUCTION);
    this.formGroup.get('server').setValue(signatureConfig.server);
    if (this.featureFlagService.enabled(FeatureFlagService.FLAGS.editEnvelopeInDocusign)()) {
      (this.formGroup.get('properties') as UntypedFormGroup).get('editEnvelope').setValue(signatureConfig.properties?.editEnvelope ?? true);
    }
    return this.formGroup;
  }

  get propertiesForm(): UntypedFormGroup {
    return (this._formGroup.get('properties') as UntypedFormGroup);
  }

  ngOnInit() {
    this._formGroup = new UntypedFormGroup({
      // hidden
      id: new UntypedFormControl(),
      providerName: new UntypedFormControl(this.name),
      sendOnBehalf: new UntypedFormControl(false), // determines if the server uses "signature impersonation" or just "signature" for the scope
      scope: new UntypedFormControl('signature impersonation'),
      name: new UntypedFormControl(this.name),
      server: new UntypedFormControl(DocusignAuthServer.DEMO),
      properties: new UntypedFormGroup({
        sendingOnBehalfOfOptions: new UntypedFormGroup({
          type: new UntypedFormControl(SendOnBehalfOf.KAD_ACCOUNT),
        }),
      }),
      // visible
      accountId: new UntypedFormControl(null, [Validators.required, Validators.pattern(DocusignComponent.UUID_REGEX)]),
      userId: new UntypedFormControl(null, [Validators.required, Validators.pattern(DocusignComponent.UUID_REGEX)]),
      useDemo: new UntypedFormControl(true),
    });

    if (this.featureFlagService.enabled(FeatureFlagService.FLAGS.editEnvelopeInDocusign)()) {
      (this._formGroup.get('properties') as UntypedFormGroup).addControl('editEnvelope', new UntypedFormControl(true));
    }

    // keep id in sync with account id
    this.formGroup.get('accountId').valueChanges.subscribe((clientId) => this.formGroup.get('id').setValue(clientId));
    // keep server in sync with useDemo
    this.formGroup
      .get('useDemo')
      .valueChanges.subscribe((useDemo) =>
        this.formGroup.get('server').setValue(useDemo ? DocusignAuthServer.DEMO : DocusignAuthServer.PRODUCTION)
      );
  }

  testCredentials(): void {
    this.testCredentialsInProgress = true;
    const dsc = this.formGroup.value;
    this.adv2ConfigService
      .testCredentials(dsc)
      .pipe(finalize(() => (this.testCredentialsInProgress = false)))
      .subscribe({
        next: (_) => this.showAlert(true, 'Credentials successfully authenticated.'),
        error: (err) => {
          console.log('test-credentials: ', err);
          if (err.error) {
            if (err.error.error === 'consent_required') {
              this.showConsentRequest();
            } else {
              const desc = err.error.error_description ? ` - ${err.error.error_description}` : '';
              this.showAlert(false, err.error.error + desc);
            }
          } else {
            const desc = `An error occurred. ${err.statusText}.`;
            this.showAlert(false, desc);
          }
        },
      });
  }

  private showConsentRequest() {
    this.adv2ConfigService
      .getConsentUrl({
        callbackUrl: environment.uiHost,
        providerName: this.name,
        server: this.formGroup.get('server').value,
      })
      .subscribe({
        next: (url) => {
          this.sps.getSignatureProviderService('docusign').getSignatureConsentService().requestConsent(url.url);
        },
        error: (err) => this.showAlert(false, err),
      });
  }

  private showAlert(ok: boolean, msg: string): void {
    this.buttonToastComponent.testSuccess.ok = ok;
    this.buttonToastComponent.testSuccess.msg = msg;
    this.buttonToastComponent.testSuccess.title = ok ? 'Authentication Success' : 'Authentication Failure';
    this.buttonToastComponent.testSuccess.show = true;
    this.buttonToastComponent.closeAlertDelay();
    this.testCredentialsInProgress = false;
  }

  protected readonly FeatureFlagService = FeatureFlagService;
}
